/**
 * Marketing block-render wiring.
 *
 * The public site renders the SAME data-driven blocks as the CMS canvas (see
 * @diality/ui blocks + BlockRenderer). Both surfaces differ only in this
 * provider: the web app supplies a react-router <Link> so block CTAs navigate
 * client-side, and a resolveImage that turns stored image refs into URLs.
 */
import type { ReactNode } from "react";
import { Link } from "react-router";
import { ORIGINAL_RENDITION } from "@diality/contracts";
import { BlockRenderProvider } from "@diality/ui";
import type { BlockLinkProps, ResolveMedia } from "@diality/ui";
import { getAnalytics } from "../analytics/configured-analytics";
import { CaptchaPreference } from "./CaptchaPreference";
import { submitContactInquiry } from "./contact-inquiry";
import { submitMailingListSignup } from "./mailing-list";

/** Internal app path (router-owned) vs. external/hash (plain anchor). */
function isInternal(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//");
}

/**
 * Renders block links via react-router for in-app paths, and a plain anchor for
 * external links (opened in a new tab) and in-page hash anchors.
 */
export function BlockRouterLink({ href, className, children }: BlockLinkProps) {
  if (isInternal(href)) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  const external = /^https?:\/\//i.test(href);
  return (
    <a
      href={href}
      className={className}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

/**
 * A stored cropId is always the crop's output size, `{w}x{h}`
 * (media.service.ts:377) — never anything else.
 */
const STORED_CROP_ID = /^\d+x\d+$/;

/**
 * Resolve an image reference to something renderable.
 *
 * A ref the CMS stored carries the asset it points at, and the two halves
 * together ARE the crop's public key: `crops/{assetId}/{cropId}.webp` under the
 * media bucket's public tier, served at `/media/*` (CloudFront in production,
 * the server's own proxy where there is no CDN in front). No lookup needed, which
 * matters — ResolveMedia is synchronous.
 *
 * Static marketing pages compose blocks in code and pass an absolute image URL
 * as the cropId instead. The two shapes cannot collide, so the cropId tells them
 * apart.
 */
export const resolveMarketingMedia: ResolveMedia = (ref) => {
  if (!ref?.cropId) return null;
  // Video FIRST, before the {w}x{h} test below. A video's cropId is the
  // ORIGINAL_RENDITION sentinel, which that regex rejects — so it would fall
  // through to the absolute-URL escape hatch and yield a URL of "original".
  // A video is delivered as uploaded, so the original IS the thing to serve.
  if (ref.mediaType === "video") {
    if (!ref.assetId) return null;
    return {
      mediaType: "video",
      url: `/media/originals/${ref.assetId}`,
      poster: `/media/thumbs/${ref.assetId}.webp`,
    };
  }
  // An UNFRAMED slot stores the picture as uploaded, so its ref names the
  // original with the same sentinel a video uses. It must be read before the
  // escape hatch below, which would otherwise yield a URL of "original".
  // Unlike a video this carries no mediaType — an absent one already reads as a
  // picture — so the sentinel is the only thing that tells them apart.
  if (ref.cropId === ORIGINAL_RENDITION) {
    if (!ref.assetId) return null;
    return { url: `/media/originals/${ref.assetId}` };
  }
  if (!STORED_CROP_ID.test(ref.cropId)) return { url: ref.cropId };
  // A stored ref that cannot name its asset cannot be resolved to a crop. Render
  // nothing rather than a URL with a hole in it.
  if (!ref.assetId) return null;
  return { url: `/media/crops/${ref.assetId}/${ref.cropId}.webp` };
};

/** Provides link + image resolution to every block rendered on the public site. */
export function MarketingBlockProvider({ children }: { children: ReactNode }) {
  return (
    <BlockRenderProvider
      Link={BlockRouterLink}
      resolveImage={resolveMarketingMedia}
      submitContactInquiry={submitContactInquiry}
      contactProtection={<CaptchaPreference />}
      submitMailingListSignup={submitMailingListSignup}
      onContactFormSubmitted={({ audience }) => {
        getAnalytics()?.publish({ type: "form-submission", audience });
      }}
    >
      {children}
    </BlockRenderProvider>
  );
}
